import React, { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, FileSpreadsheet, Download, Calendar, Loader2 } from 'lucide-react';
import { downloadFile } from '@/lib/api';
import { toast } from 'sonner';

const BULAN = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

const REPORTS = [
  { key: 'agenda', label: 'Laporan Agenda Pimpinan', desc: 'Daftar seluruh agenda pimpinan dengan status dan delegasi', icon: Calendar, color: 'red', filter: 'periodStatus' },
  { key: 'sppd', label: 'Laporan Perjalanan Dinas (SPPD)', desc: 'Monitoring SPPD lengkap dengan tujuan dan biaya', icon: FileText, color: 'gold', filter: 'periodStatus' },
  { key: 'kehadiran', label: 'Laporan Kehadiran Pimpinan', desc: 'Rekap kehadiran pimpinan pada agenda resmi', icon: FileText, color: 'blue', filter: 'period' },
  { key: 'delegasi', label: 'Laporan Delegasi Agenda', desc: 'Agenda yang didelegasikan kepada pejabat lain', icon: FileText, color: 'green', filter: 'period' },
  { key: 'bulanan', label: 'Laporan Bulanan Prokopim', desc: 'Rekap lengkap bulanan: agenda + SPPD + statistik', icon: FileText, color: 'red', filter: 'month' },
];

export default function ReportsPage() {
  const now = new Date();
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    status: 'all',
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });
  const [downloading, setDownloading] = useState({ key: '', format: '' });

  const download = async (key, format) => {
    setDownloading({ key, format });
    try {
      const params = new URLSearchParams();
      const r = REPORTS.find((x) => x.key === key);
      if (r.filter === 'month') {
        params.set('year', filters.year);
        params.set('month', filters.month);
      } else {
        if (filters.start_date) params.set('start_date', filters.start_date);
        if (filters.end_date) params.set('end_date', filters.end_date);
        if (r.filter === 'periodStatus' && filters.status !== 'all') params.set('status', filters.status);
      }
      const fname = key === 'bulanan'
        ? `Laporan_Bulanan_${BULAN[filters.month - 1]}_${filters.year}.${format === 'pdf' ? 'pdf' : 'xlsx'}`
        : `Laporan_${key}_Prokopim.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      await downloadFile(`/reports/${key}/${format}?${params.toString()}`, fname);
      toast.success(`Laporan ${format.toUpperCase()} berhasil diunduh`);
    } catch (e) {
      toast.error('Gagal mengunduh laporan');
    } finally {
      setDownloading({ key: '', format: '' });
    }
  };

  const accents = {
    red: 'bg-red-50 text-red-600',
    gold: 'bg-amber-50 text-amber-600',
    blue: 'bg-sky-50 text-sky-600',
    green: 'bg-emerald-50 text-emerald-600',
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Laporan PROAKTIF"
        subtitle="Generate laporan resmi keprotokolan dalam format PDF & Excel dengan template Pemkot Makassar."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Laporan' }]}
      />

      {/* Filter card */}
      <Card className="border-slate-200 shadow-premium">
        <CardHeader><CardTitle className="text-base font-bold">Filter Periode</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label>Tanggal Mulai (untuk Agenda/SPPD/Kehadiran/Delegasi)</Label>
            <Input data-testid="report-start-date" type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Tanggal Akhir</Label>
            <Input data-testid="report-end-date" type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Status (Agenda / SPPD)</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Approved">Approved</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
                <SelectItem value="Selesai">Selesai</SelectItem>
                <SelectItem value="Sedang Dinas">Sedang Dinas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Periode Bulanan</Label>
            <div className="flex gap-2">
              <Select value={String(filters.month)} onValueChange={(v) => setFilters({ ...filters, month: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BULAN.map((b, i) => <SelectItem key={i} value={String(i + 1)}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" value={filters.year} onChange={(e) => setFilters({ ...filters, year: Number(e.target.value) })} className="w-24" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report list */}
      <div className="grid md:grid-cols-2 gap-4">
        {REPORTS.map((r) => (
          <Card key={r.key} className="border-slate-200 shadow-premium hover:shadow-elevated transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${accents[r.color]}`}>
                  <r.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900">{r.label}</h3>
                  <p className="text-xs text-slate-500 mt-1">{r.desc}</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button
                      size="sm"
                      data-testid={`download-${r.key}-pdf`}
                      onClick={() => download(r.key, 'pdf')}
                      disabled={downloading.key === r.key && downloading.format === 'pdf'}
                      className="bg-[#DC2626] hover:bg-[#B91C1C]"
                    >
                      {downloading.key === r.key && downloading.format === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                      Download PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`download-${r.key}-excel`}
                      onClick={() => download(r.key, 'excel')}
                      disabled={downloading.key === r.key && downloading.format === 'excel'}
                    >
                      {downloading.key === r.key && downloading.format === 'excel' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                      Download Excel
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
